import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Star, Check, EyeOff, Pencil, X } from "lucide-react";

const TABS = [["pending", "Pending"], ["approved", "Approved"], ["hidden", "Hidden"]];
const STATUS_CLS = { pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-green-50 text-green-700 border-green-200", hidden: "bg-stone-100 text-stone-500 border-stone-200" };

const Summary = ({ s }) => {
  if (!s) return null;
  const max = Math.max(1, ...Object.values(s.breakdown));
  const stat = (label, value, testid) => (
    <Card className="rounded-xl p-4" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wide text-stone-400 font-semibold">{label}</p>
      <p className="mt-1 text-2xl font-bold text-stone-900">{value}</p>
    </Card>
  );
  return (
    <div className="grid sm:grid-cols-4 gap-3" data-testid="reviews-summary">
      <Card className="rounded-xl p-4" data-testid="reviews-avg">
        <p className="text-[11px] uppercase tracking-wide text-stone-400 font-semibold">Average rating</p>
        <div className="mt-1 flex items-center gap-2">
          <span className="text-2xl font-bold text-stone-900">{s.average ?? "-"}</span>
          <div className="flex">{[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-4 h-4 ${s.average && n <= Math.round(s.average) ? "fill-[#FF7A00] text-[#FF7A00]" : "text-stone-200"}`} />)}</div>
        </div>
      </Card>
      {stat("Total reviews", s.total, "reviews-total")}
      {stat("Live on website", s.live, "reviews-live")}
      <Card className="rounded-xl p-4 space-y-1" data-testid="reviews-breakdown">
        {Object.entries(s.breakdown).sort((a, b) => b[0] - a[0]).map(([star, n]) => (
          <div key={star} className="flex items-center gap-2 text-[11px] text-stone-500">
            <span className="w-6">{star}★</span>
            <div className="flex-1 h-1.5 rounded-full bg-stone-100 overflow-hidden"><div className={`h-full ${+star >= 3 ? "bg-[#FF7A00]" : "bg-red-400"}`} style={{ width: `${(n / max) * 100}%` }} /></div>
            <span className="w-5 text-right">{n}</span>
          </div>
        ))}
      </Card>
    </div>
  );
};

const ReviewCard = ({ r, onSave }) => {
  const [edit, setEdit] = useState(false);
  const [story, setStory] = useState(r.story);
  const [name, setName] = useState(r.display_name);
  const [busy, setBusy] = useState(false);
  const save = async (body) => { setBusy(true); const ok = await onSave(r.review_id, body); setBusy(false); if (ok) setEdit(false); };
  return (
    <Card className="rounded-xl p-5 space-y-3" data-testid={`review-card-${r.review_id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex">{[1, 2, 3, 4, 5].map(n => <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "fill-[#FF7A00] text-[#FF7A00]" : "text-stone-200"}`} />)}</div>
        <span className="text-sm font-semibold text-stone-900">{r.patient_name || "Patient"}</span>
        <span className="text-xs text-stone-400">{r.created_at?.split("T")[0]}</span>
        <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_CLS[r.status]}`}>{r.status}</Badge>
        {r.rating < 3 && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200" data-testid={`review-low-${r.review_id}`}>Below 3 stars, not for website</Badge>}
        {!r.publish_consent && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200" data-testid={`review-no-consent-${r.review_id}`}>No permission to publish</Badge>}
      </div>
      {edit ? (
        <div className="space-y-2">
          <Textarea value={story} maxLength={600} rows={4} onChange={e => setStory(e.target.value)} className="rounded-lg text-sm" data-testid={`review-edit-story-${r.review_id}`} />
          <Input value={name} maxLength={60} onChange={e => setName(e.target.value)} className="rounded-lg text-sm max-w-xs" data-testid={`review-edit-name-${r.review_id}`} />
          {r.original_story !== story && <p className="text-[11px] text-stone-400">Original: "{r.original_story}"</p>}
        </div>
      ) : (
        <>
          <blockquote className="text-sm text-stone-700 leading-relaxed">"{r.story}"</blockquote>
          <p className="text-xs text-stone-500">Shown as <b>{r.display_name}</b>{r.story !== r.original_story && " · edited"}</p>
        </>
      )}
      <div className="flex gap-2 flex-wrap">
        {edit ? (<>
          <Button size="sm" className="text-xs rounded-lg bg-stone-900 text-white" disabled={busy} onClick={() => save({ story, display_name: name })} data-testid={`review-save-${r.review_id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}Save</Button>
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setEdit(false); setStory(r.story); setName(r.display_name); }}><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
        </>) : (<>
          {r.status !== "approved" && <Button size="sm" className="text-xs rounded-lg bg-green-600 hover:bg-green-700 text-white" disabled={busy || !r.publish_consent || r.rating < 3} title={r.rating < 3 ? "Reviews below 3 stars can't go on the website" : !r.publish_consent ? "Patient did not give permission to publish" : ""} onClick={() => save({ status: "approved" })} data-testid={`review-approve-${r.review_id}`}><Check className="w-3.5 h-3.5 mr-1" />Approve for website</Button>}
          {r.status !== "hidden" && <Button size="sm" variant="outline" className="text-xs rounded-lg" disabled={busy} onClick={() => save({ status: "hidden" })} data-testid={`review-hide-${r.review_id}`}><EyeOff className="w-3.5 h-3.5 mr-1" />Hide</Button>}
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => setEdit(true)} data-testid={`review-edit-${r.review_id}`}><Pencil className="w-3.5 h-3.5 mr-1" />Fix typos</Button>
        </>)}
      </div>
    </Card>
  );
};

export default function ReviewsPage() {
  const { token } = useAuth();
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/staff/reviews?status=${tab}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(setData).catch(() => setData({ reviews: [], counts: {} }));
  }, [tab, token]);
  useEffect(() => { setData(d => d && { ...d, reviews: null }); load(); }, [load]);

  const onSave = async (id, body) => {
    const r = await fetch(`${API}/staff/reviews/${id}`, { method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const d = await r.json();
    if (!r.ok) { toast.error(d.detail || "Could not update"); return false; }
    toast.success(body.status === "approved" ? "Approved. It now shows on the website." : body.status === "hidden" ? "Review hidden" : "Saved");
    load(); return true;
  };

  return (
    <div className="space-y-5" data-testid="reviews-page">
      <div>
        <h1 className="text-2xl font-bold text-stone-900">Reviews</h1>
        <p className="text-sm text-stone-500">Patient reviews. Approved reviews (3 stars or more, with permission) appear in Patient Stories on the Programme page. Reviews below 3 stars create a follow-up task. Ask for reviews from a patient's profile.</p>
      </div>
      <Summary s={data?.summary} />
      <div className="flex gap-2">
        {TABS.map(([k, label]) => (
          <Button key={k} size="sm" variant={tab === k ? "default" : "outline"} className={`text-xs rounded-lg ${tab === k ? "bg-stone-900 text-white" : ""}`} onClick={() => setTab(k)} data-testid={`reviews-tab-${k}`}>
            {label}{data?.counts?.[k] != null && <span className="ml-1.5 opacity-70">{data.counts[k]}</span>}
          </Button>
        ))}
      </div>
      {!data?.reviews ? <Loader2 className="w-6 h-6 animate-spin text-stone-400" /> : data.reviews.length ? (
        <div className="space-y-3">{data.reviews.map(r => <ReviewCard key={r.review_id} r={r} onSave={onSave} />)}</div>
      ) : <Card className="rounded-xl p-8 text-center text-sm text-stone-500" data-testid="reviews-empty">No {tab} reviews.</Card>}
    </div>
  );
}
